import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Leaf } from "lucide-react";
import { fetchFormularioPublico, submitFormularioPublico } from "../api";
import type { FormularioPublico } from "../types";

/**
 * Landing pública de captación. Accesible sin login en /f/:slug.
 * Pensada para embeber en webs externas vía iframe.
 */
export function FormularioPublicoPage({ slug }: { slug: string }) {
  const [form, setForm] = useState<FormularioPublico | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFormularioPublico(slug)
      .then((f) => setForm(f))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Captura UTM de la URL para atribución
  function utmParams(): Record<string, string> {
    const p = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign"]) {
      const v = p.get(k);
      if (v) out[k] = v;
    }
    return out;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await submitFormularioPublico(slug, { ...values, ...utmParams() });
      setSuccess(res.mensaje);
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { message?: string } } };
      setError(e2?.response?.data?.message ?? "No se pudo enviar el formulario.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-emerald-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {loading && (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-20">
            <Loader2 className="animate-spin" size={20} /> Cargando…
          </div>
        )}

        {notFound && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
            <p className="text-slate-700 font-bold">Formulario no disponible</p>
            <p className="text-sm text-slate-400 mt-1">El enlace no existe o ha sido desactivado.</p>
          </div>
        )}

        {form && !success && (
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/70 overflow-hidden">
            <div className="bg-gradient-to-br from-brand-700 to-emerald-600 px-6 py-5 text-white">
              <div className="flex items-center gap-2 mb-1 opacity-80">
                <Leaf size={16} />
                <span className="text-xs font-black uppercase tracking-widest">Inpronexus</span>
              </div>
              <h1 className="text-xl font-black">{form.titulo}</h1>
              {form.descripcion && (
                <p className="text-sm text-emerald-50/90 mt-1">{form.descripcion}</p>
              )}
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              {error && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
                  {error}
                </div>
              )}
              {form.campos.map((campo) => (
                <div key={campo.key}>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {campo.label}
                    {campo.requerido && <span className="text-rose-500"> *</span>}
                  </label>
                  <input
                    type={campo.tipo === "email" ? "email" : campo.tipo === "tel" ? "tel" : "text"}
                    required={campo.requerido}
                    value={values[campo.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [campo.key]: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none"
                  />
                </div>
              ))}
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-brand-700 to-emerald-600 hover:from-brand-800 hover:to-emerald-700 text-white px-5 py-3 font-bold text-sm shadow-lg disabled:opacity-50 transition"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                Enviar
              </button>
              <p className="text-[11px] text-slate-400 text-center">
                Tus datos se tratarán conforme a nuestra política de privacidad.
              </p>
            </form>
          </div>
        )}

        {success && (
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/70 p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-black text-slate-900">¡Recibido!</h2>
            <p className="text-slate-500 mt-2">{success}</p>
          </div>
        )}
      </div>
    </div>
  );
}
